import './accessRelatedDataForFiles.less'
import {
  ACTION_FETCH_PCI_ACCESS_RELATED_DATA,
  fetchPCIAccessRelatedData
} from '../../../complianceDashboardSlice'
import ChartBarSimple from '../../../../../../components/ChartBarSimple/chartBarSimple'
import CustomTooltip from '../../../../../../components/ChartTooltipV2'
import LoaderView from '../../../../../../components/Loader/loader'
import useWindowSize from '../../../../../../hooks/useWindowResize'
import { getShowLoader } from '../../../../../../reducers/requestReducer'
import { RootState } from '../../../../../../rootReducer'
import { getWindowProps } from '../../../../../../utils'
import {
  UNIQUE_WIDGET_INDEX,
  getUniqueWidgetIndex,
  ChartColors
} from '../../../../mainDashboardv2/constants'
import { AttributeSetData, PCIAccessRelatedData } from '../../../queries'
import { mockPCIAccessRelatedData } from '../../mocks'
import WidgetTitle from '../../../../../../components/WidgetTitle'
import Card from '../../../../../accessControl/components/card'
import { AccessControlAccessTypes } from '../../../../../accessControl/types'
import historyService from '../../../../../../services/historyService'
import { getUpdatedUrl } from '../../../../../../utils/urlUtil'
import {
  DATA_SOURCE_TYPE,
  FILTER_ACCESS,
  PAGE_VIEW_KEY,
  PAGE_VIEW_VALUES,
  URL_ACCESS_CONTROL,
  URL_DATA_SOURCES,
  URL_OBJECTS_ACCESS,
  URL_PII_OBJECTS
} from '../../../../../../constants'
import EmptyStateIcon from '../../../../../../assets/inlineSvg/pci-access-type-widget-empty-icon.svg'
import { hasData } from '../../helper'
import { EmptyState } from '../../emptyState/emptyState'
import { useDashboardType } from '../../hooks/useDashboardType'
import { DashboardCategory } from '../../../../mainDashboardv2/slice'
import { Typography, Icon, Tooltip } from '@lightbeamai/design-system'
import React, { useEffect, useState, useMemo } from 'react'
import { FormattedNumber, useIntl } from 'react-intl'
import { connect } from 'react-redux'

interface OwnProps {
  useMockData?: boolean
  chartBarAlignment?: 'vertical' | 'horizontal'
  widgetDatasourceId?: string
  widgetDashboardId?: string
  widgetDashboardType?: string
  attributeSetId?: string
}

interface StateProps {
  widget?: PCIAccessRelatedData | null
  attributeSetData?: AttributeSetData | null
  showLoader: boolean
}

interface DispatchProps {
  fetchPCIAccessRelatedData: (params: {
    attributeSetId: string
    [UNIQUE_WIDGET_INDEX]?: string
  }) => void
}

type AccessRelatedDataForFilesComponentProps = StateProps & DispatchProps & OwnProps

const AccessRelatedDataForFiles = (props: AccessRelatedDataForFilesComponentProps) => {
  const {
    widget,
    showLoader,
    chartBarAlignment = 'horizontal',
    useMockData = false,
    widgetDatasourceId,
    widgetDashboardId,
    widgetDashboardType,
    attributeSetData,
    fetchPCIAccessRelatedData
  } = props

  const intl = useIntl()

  const windowProps = getWindowProps(useWindowSize())
  const { isLargeScreen } = windowProps

  const { isPciDashboard } = useDashboardType(widgetDashboardType as DashboardCategory)

  const titleTabs = [
    {
      key: AccessControlAccessTypes.open,
      name: intl.formatMessage({
        id: 'compliance.dashboard.pci.widget.accessRelatedData.tab.open'
      })
    },
    {
      key: AccessControlAccessTypes.excessive,
      name: intl.formatMessage({
        id: 'compliance.dashboard.pci.widget.accessRelatedData.tab.excessive'
      })
    }
  ]

  const [activeTab, setActiveTab] = useState<AccessControlAccessTypes>(titleTabs[0].key)
  const handleTabChange = (tab) => setActiveTab(tab)
  const isOpen = activeTab === AccessControlAccessTypes.open

  useEffect(() => {
    if (useMockData) return
    if (!attributeSetData?.id) return
    fetchPCIAccessRelatedData({
      attributeSetId: attributeSetData?.id || '',
      [UNIQUE_WIDGET_INDEX]: getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
    })
  }, [
    useMockData,
    fetchPCIAccessRelatedData,
    attributeSetData,
    widgetDashboardId,
    widgetDatasourceId
  ])

  const widgetData = useMemo(() => {
    if (isOpen) {
      return widget?.datasourcesWithOpenAccessCount || []
    } else {
      return widget?.datasourcesWithExcessAccessCount || []
    }
  }, [widget, activeTab])

  const chartsData = useMemo(() => {
    const top5Data = widgetData.slice(0, 5)
    return top5Data.map((item) => ({
      name: item.datasourceName,
      value: item.objectsCount,
      color: isOpen ? ChartColors.Orange : ChartColors.Red,
      dataKey: item.datasourceId,
      type: item.datasourceType
    }))
  }, [widgetData, activeTab])

  const countsFormatter = (value: number) => intl.formatNumber(value, { notation: 'compact' })

  const handleClick = (val: any) => {
    const datasourceId = val?.dataKey || val?.payload?.dataKey
    const datasourceType = val?.type || val?.payload?.type
    historyService.push(
      getUpdatedUrl({
        pathName: `${URL_DATA_SOURCES}/${datasourceId}${URL_OBJECTS_ACCESS}`,
        pageName: URL_OBJECTS_ACCESS,
        queryParams: {
          [DATA_SOURCE_TYPE]: datasourceType
        },
        pageQueryParams: {
          [FILTER_ACCESS]: activeTab,
          [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.card
        }
      })
    )
  }

  const handleTotalAccessTypeClick = () => {
    historyService.push(
      getUpdatedUrl({
        pathName: `${URL_ACCESS_CONTROL}${URL_PII_OBJECTS}`,
        pageName: URL_PII_OBJECTS,
        pageQueryParams: {
          [FILTER_ACCESS]: activeTab
        }
      })
    )
  }

  const cardTitle = (
    <WidgetTitle
      title={
        <div className="widget-title">
          <Typography color="primary" variant="h6" as="span">
            {intl.formatMessage({
              id: 'compliance.dashboard.pci.widget.accessRelatedData.title'
            })}
          </Typography>
          <Tooltip
            title={intl.formatMessage({
              id: isPciDashboard
                ? 'compliance.dashboard.pci.widget.title.tooltip'
                : 'compliance.dashboard.hipaa.widget.title.tooltip'
            })}
            placement="right"
          >
            <Icon size={16} name="InfoCircle" className="pointer" />
          </Tooltip>
        </div>
      }
      tabs={titleTabs}
      onChange={handleTabChange}
      activeTab={activeTab}
      bordered
    />
  )

  const hasChartData = hasData(chartsData)

  return (
    <Card className="access-related-data-files-widget" cardTitle={cardTitle}>
      <div className={`access-related-data-files-chart-wrapper ${chartBarAlignment}`}>
        <div className="dashboard-labels-count-wrapper">
          <div className="label-items">
            <div className="label-item" onClick={handleTotalAccessTypeClick}>
              <Typography color="primary" type="strong" variant="h4">
                <FormattedNumber
                  value={
                    (isOpen ? widget?.openAccessObjectsCount : widget?.excessAccessObjectsCount) ||
                    0
                  }
                  notation="compact"
                />
              </Typography>
              <Typography color="secondary" type="medium" variant="baseMinus2">
                {intl.formatMessage(
                  {
                    id: 'compliance.dashboard.pci.widget.accessRelatedData.summary.accessType'
                  },
                  { accessType: activeTab }
                )}
              </Typography>
            </div>
            <div className="label-item" onClick={handleTotalAccessTypeClick}>
              <Typography color="primary" type="strong" variant="h4">
                <FormattedNumber
                  value={
                    (isOpen
                      ? widget?.datasourcesWithOpenAccessCount?.length
                      : widget?.datasourcesWithExcessAccessCount?.length) || 0
                  }
                  notation="compact"
                />
              </Typography>
              <Typography color="secondary" type="medium" variant="baseMinus2">
                {intl.formatMessage({
                  id: 'compliance.dashboard.pci.widget.accessRelatedData.summary.datasources'
                })}
              </Typography>
            </div>
          </div>
        </div>
        {hasChartData ? (
          <div className="charts-wrapper access-related-data-files-bar-charts">
            <Typography as="span" variant="baseMinus2" className="xs-ml-32">
              {intl.formatMessage({
                id: 'compliance.dashboard.pci.widget.accessRelatedData.chartTitle'
              })}
            </Typography>
            <ChartBarSimple
              className="xs-mt-8 v2-chart"
              data={chartsData}
              cartesianGridHorrizontal
              tickFormatter={{
                fontSize: '0.72rem',
                fill: ChartColors.SecondaryText,
                width: 60,
                textAnchor: 'middle'
              }}
              multiColored
              chartMargin={{ left: 20, right: 20, top: 0, bottom: 0 }}
              barSize={60}
              chartResponsiveHeight={isLargeScreen ? 240 : 200}
              {...(isLargeScreen ? { xAxisHeight: 55 } : {})}
              yLabel={intl.formatMessage({
                id: 'compliance.dashboard.pci.widget.accessRelatedData.chart.ylabel'
              })}
              xAxisAngle={0}
              YTickFormatter={countsFormatter}
              yAxisLabelPadding={{ dx: -12, dy: -6 }}
              customToolTip={<CustomTooltip />}
              tickMargin={4}
              xAxisTickInterval={0}
              onBarItemClick={handleClick}
            />
          </div>
        ) : (
          <EmptyState
            title={intl.formatMessage(
              {
                id: 'compliance.dashboard.pci.widget.accessRelatedData.errorText'
              },
              { accessType: activeTab }
            )}
            icon={<EmptyStateIcon />}
          />
        )}
      </div>
      <LoaderView size="small" showLoader={showLoader} />
    </Card>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => ({
  widget: ownProps?.useMockData
    ? mockPCIAccessRelatedData.widget
    : state.complianceDashboard.pciAccessRelatedData?.[
        getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
      ],
  attributeSetData: state.complianceDashboard.attributeSetData,
  showLoader: getShowLoader(ACTION_FETCH_PCI_ACCESS_RELATED_DATA)
})

const mapDispatchToProps: DispatchProps = {
  fetchPCIAccessRelatedData
}

export default connect(mapStateToProps, mapDispatchToProps)(AccessRelatedDataForFiles)
